import { Link } from 'react-router-dom'
import { useMyDay } from '../api/queries'
import type { ScheduleAbsence, ScheduleNextAction, ScheduleTask, WorkAssignment } from '../api/types'
import { AddressLink, PhoneLink } from './SiteContact'
import { Card } from './ui'

/** yyyy-MM-dd for i dag i lokal tid - ikke UTC, som ville skifte dag om aftenen. */
function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

/**
 * Klokkeslæt til rækkefølgen. Poster uden tid lægger sig sidst - de er ikke
 * mindre vigtige, men de har ingen plads i rækken.
 */
function sortKey(assignment: WorkAssignment) {
  return assignment.startTime ?? '99:99'
}

/**
 * Dagens opgaver: montørens egen dag, øverst på forsiden (beslutning 061).
 *
 * <p>
 * Kalenderens dagsvisning er bygget til kontoret - den viser hele holdet. Den
 * her viser <b>kun mine egne jobs, i den rækkefølge de skal laves</b>, med det
 * han skal bruge i bilen: klokkeslæt, kunde, adresse med kortopslag og et nummer
 * at ringe til.
 * </p>
 *
 * Kortet vises kun, når der faktisk står noget - en tom "dagens opgaver" på
 * forsiden hver morgen lærer folk at ignorere den.
 */
export function MyDayCard() {
  const { data, isLoading } = useMyDay(today())

  if (isLoading || !data) return null

  const assignments = [...data.assignments].sort((a, b) => sortKey(a).localeCompare(sortKey(b)))
  const absences = data.absences ?? []
  const hasSomething =
    assignments.length > 0 ||
    data.tasks.length > 0 ||
    data.nextActions.length > 0 ||
    absences.length > 0

  if (!hasSomething) return null

  return (
    <Card title="Dagens opgaver">
      <div className="stack" style={{ gap: 10 }}>
        {/* Staar der fravaer paa dagen, siges det foerst - saa ved man, hvorfor
            der ikke staar mere (beslutning 053). */}
        {absences.map((a) => (
          <AbsenceRow key={a.id} absence={a} />
        ))}

        {assignments.map((a) => (
          <JobRow key={a.id} assignment={a} />
        ))}

        {data.tasks.map((t) => (
          <TaskRow key={t.taskId} task={t} />
        ))}

        {data.nextActions.map((n) => (
          <NextActionRow key={n.projectId} action={n} />
        ))}
      </div>
    </Card>
  )
}

function AbsenceRow({ absence }: { absence: ScheduleAbsence }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'center' }}>
      <span
        style={{ width: 4, alignSelf: 'stretch', minHeight: 28, borderRadius: 2, background: absence.color }}
      />
      <div style={{ fontWeight: 600, color: absence.color }}>{absence.typeName}</div>
    </div>
  )
}

function JobRow({ assignment }: { assignment: WorkAssignment }) {
  const time = assignment.startTime
    ? assignment.endTime
      ? `${assignment.startTime}-${assignment.endTime}`
      : assignment.startTime
    : 'Hele dagen'

  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <span
        style={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 44,
          borderRadius: 2,
          background: assignment.projectStatusColor,
          flexShrink: 0
        }}
      />

      <div style={{ minWidth: 62, flexShrink: 0 }}>
        <div className="small" style={{ fontWeight: 600 }}>
          {time}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 150 }}>
        <div style={{ fontWeight: 600 }}>
          {assignment.projectId ? (
            <Link to={`/projects/${assignment.projectId}`}>
              {assignment.projectOrderNumber && <span className="cal-chip__order">{assignment.projectOrderNumber}</span>}
              {assignment.projectName}
            </Link>
          ) : (
            assignment.projectName
          )}
        </div>

        {assignment.taskTitle && <div className="small">{assignment.taskTitle}</div>}

        <div className="small muted">
          {assignment.projectCustomer}
          {assignment.deviceName && ` · ${assignment.deviceName}`}
        </div>

        {/* Adressen og nummeret er det, han skal bruge paa vejen derhen
            (beslutning 025). */}
        {assignment.site && (
          <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
            <AddressLink contact={assignment.site} />
            {assignment.site.phone && <PhoneLink phone={assignment.site.phone} />}
          </div>
        )}

        {assignment.note && <div className="small">{assignment.note}</div>}
      </div>
    </div>
  )
}

function TaskRow({ task }: { task: ScheduleTask }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <span
        style={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 32,
          borderRadius: 2,
          background: task.statusColor,
          opacity: 0.5,
          flexShrink: 0
        }}
      />
      <div style={{ minWidth: 62, flexShrink: 0 }}>
        <div className="tiny muted">{task.isOverdue ? 'Forsinket' : 'Opgave'}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div className="small" style={{ fontWeight: 600 }}>
          <Link to={`/projects/${task.projectId}`}>{task.title}</Link>
        </div>
        <div className="tiny muted">
          {task.projectName} · {task.statusName}
        </div>
      </div>
    </div>
  )
}

function NextActionRow({ action }: { action: ScheduleNextAction }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
      <span
        style={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 32,
          borderRadius: 2,
          background: action.statusColor,
          opacity: 0.5,
          flexShrink: 0
        }}
      />
      <div style={{ minWidth: 62, flexShrink: 0 }}>
        <div className="tiny muted">{action.isOverdue ? 'Forsinket' : 'Næste'}</div>
      </div>
      <div style={{ flex: 1 }}>
        <div className="small" style={{ fontWeight: 600 }}>
          <Link to={`/projects/${action.projectId}`}>{action.text}</Link>
        </div>
        <div className="tiny muted">
          {action.projectName}
          {action.projectCustomer && ` · ${action.projectCustomer}`}
        </div>
      </div>
    </div>
  )
}
